import { useQuery } from '@tanstack/react-query'

import config from '@config'
import { getUserType } from '@utils/chatbot'
import { TAnswerResponseV2 } from '@utils/chatbot/types'
import useUser from '@hooks/useUser'
import { AnswerType, QuestionType } from '@components/Search/AISearch/types'

const {
  aiSearch: { answerUrl, chatUrl },
} = config

export const getAnswer = async ({
  questionId,
  userType,
}: {
  questionId: string
  userType: string
}) => {
  const url = new URL(answerUrl)

  url.searchParams.append('publication', 'blick')
  url.searchParams.append('questionId', questionId)
  url.searchParams.append('widgetId', 'questionWidget')
  url.searchParams.append('context', 'home')

  if (userType) {
    url.searchParams.append('userType', userType)
  }

  const response = await fetch(url, {
    method: 'GET',
  })

  if (!response.ok) {
    throw new Error('Error fetching answers.')
  }

  return (await response.json()) as Promise<TAnswerResponseV2>
}

export const getChat = async ({
  isSuggestedQuestion,
  text,
  userType,
  context,
}: {
  isSuggestedQuestion: boolean
  text: string
  userType: string
  context: string
}) => {
  const url = new URL(chatUrl)

  const bodyPayload = {
    publication: 'blick',
    text,
    context,
    isSuggestedQuestion,
    userType,
  }

  const response = await fetch(url, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json; charset=utf-8',
    },
    method: 'POST',
    body: JSON.stringify(bodyPayload),
  })

  if (!response.ok) {
    throw new Error('Error fetching chat.')
  }

  return (await response.json()) as Promise<TAnswerResponseV2>
}

const useAISearchAnswer = ({
  context = 'home',
  question,
  noResultTrack,
}: {
  question?: QuestionType | null
  context?: string
  noResultTrack?: (extraData: { searchTerm: string | undefined }) => void
}) => {
  const user = useUser()
  const userType = getUserType(user?.email)

  const { data, error, isLoading } = useQuery<AnswerType>({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: ['aiSearchAnswers', question?.id],
    queryFn: async () => {
      const answerResult = await (question?.isSuggestedQuestion
        ? getAnswer({
            questionId: question?.id,
            userType,
          })
        : getChat({
            text: question?.text ?? '',
            isSuggestedQuestion: question?.isSuggestedQuestion ?? false,
            context,
            userType,
          }))

      const {
        content,
        links,
        traceId,
        spanId,
        conversationId,
        intent,
        no_answer,
      } = answerResult

      if (no_answer) {
        noResultTrack?.({ searchTerm: question?.text })
      }

      return {
        id: `user-answer-${new Date().getTime()}`,
        type: 'answer' as const,
        content,
        links,
        traceId,
        spanId,
        conversationId,
        questionData: question,
        intent,
        noAnswer: no_answer,
      }
    },
    enabled: !!question,
  })

  return {
    data,
    error,
    isLoading,
  }
}

export default useAISearchAnswer
